import { useState } from 'react';
import { FileText, Braces, ShieldCheck, Mail } from 'lucide-react';
import ActionButton from '../Buttons/ActionButton';
import FiscalEventService from '../../services/fiscal/FiscalEventService';
import useAlerts from '../../hooks/useAlerts';
import useRetornoEmail from '../../hooks/email/useRetornoEmail';
import { FiscalEventStatus, FiscalEventType } from '../../types/fiscal/FiscalEvent';

interface Props {
  eventId: number;
  eventType?: FiscalEventType;
  status?: FiscalEventStatus;
}

export default function FiscalEventDownloads({ eventId, eventType, status }: Props) {
  const [busy, setBusy] = useState<string>();
  const { show } = useAlerts();
  const { sendRetornoEmail } = useRetornoEmail();

  const download = async (kind: 'pdf' | 'unsigned' | 'signed') => {
    if (busy) return;
    setBusy(kind);
    try {
      if (kind === 'pdf') await FiscalEventService.downloadPdf(eventId);
      else if (kind === 'unsigned') await FiscalEventService.downloadUnsignedJson(eventId);
      else await FiscalEventService.downloadSignedJson(eventId);
    } catch { show('error', 'Descarga no disponible', 'No se pudo descargar el archivo del evento fiscal.'); }
    finally { setBusy(undefined); }
  };

  const sendEmail = async () => {
    if (busy) return;
    setBusy('email');
    try { await sendRetornoEmail(eventId); }
    finally { setBusy(undefined); }
  };

  const canSendEmail = eventType === 'RETURN' && status === 'PROCESSED';

  return <div className="flex flex-wrap gap-2">
    <ActionButton
      label="PDF"
      icon={<FileText className="h-4 w-4" />}
      variant="secondary"
      size="sm"
      fullWidth={false}
      disabled={!!busy}
      loading={busy === 'pdf'}
      onClick={() => download('pdf')}
    />
    <ActionButton
      label="JSON"
      icon={<Braces className="h-4 w-4" />}
      variant="secondary"
      size="sm"
      fullWidth={false}
      disabled={!!busy}
      loading={busy === 'unsigned'}
      onClick={() => download('unsigned')}
    />
    <ActionButton
      label="JSON firmado"
      icon={<ShieldCheck className="h-4 w-4" />}
      variant="secondary"
      size="sm"
      fullWidth={false}
      disabled={!!busy}
      loading={busy === 'signed'}
      onClick={() => download('signed')}
    />
    {canSendEmail && (
      <ActionButton
        label="Enviar por correo"
        icon={<Mail className="h-4 w-4" />}
        variant="secondary"
        size="sm"
        fullWidth={false}
        disabled={!!busy}
        loading={busy === 'email'}
        onClick={sendEmail}
      />
    )}
  </div>;
}
